class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.country = this.country = this.dataset.countryIso;
    this.bgDomain = this.dataset.bgDomain;
    this.intDomain = this.dataset.intDomain;
  }

  connectedCallback() {
    if (window.location.hash === '#cart-open') {
      this.open();
    }
    this.querySelector('#CartDrawer-Checkout').addEventListener('click', this.onCheckoutButtonClick.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    if (window.location.hash === '#cart-drawer') {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (parsedState) this.classList.remove('is-empty');

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.querySelector('#CartDrawer-Checkout').addEventListener('click', this.onCheckoutButtonClick.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  async onCheckoutButtonClick(e) {
    const currencyDropdown = document.querySelector('.currency-switcher[name="doubly-currencies"]');
    const selectedCurrency = currencyDropdown ? currencyDropdown.value : null;
    let domain;
    if (selectedCurrency == 'BGN') {
      e.preventDefault();
      domain = this.bgDomain;
    }

    try {
      const cart = await getCart();

      const checkoutLink = await generateCheckoutLink(this, domain, cart);
      if (checkoutLink) {
        window.location.href = domain + checkoutLink;
      }
    } catch (error) {
      console.error('Error during checkout:', error);
    }
  }
}

async function generateCheckoutLink(el, domain, cart) {
  if (cart.item_count === 0) return '';
  let checkoutLink = '';
  try {
    const promises = cart.items.map(async (item, index) => {
      const response = await fetch(`${domain}/products/${item.handle}.js`);
      const data = await response.json();

      const variant = data.variants.find((e) => e.title === item.variant_title) || data.variants[0];
      const sellingPlan = data.selling_plan_groups?.[0]?.selling_plans?.find(
        (e) => e.name === item.selling_plan_allocation?.selling_plan?.name
      );

      let itemParams = `items[${index}][id]=${variant.id}%26items[${index}][quantity]=${item.quantity}`;
      if (sellingPlan) {
        itemParams += `%26items[${index}][selling_plan]=${sellingPlan.id}`;
      }

      return itemParams;
    });

    const itemsParams = await Promise.all(promises);
    checkoutLink = `/cart/clear?return_to=/cart/add?${itemsParams.join('%26')}%26return_to=/checkout`;

    return checkoutLink;
  } catch (error) {
    console.error('Error generating checkout link:', error);
    return '';
  }
}

async function getCart() {
  const result = await fetch('/cart.json');

  if (result.status === 200) {
    return result.json();
  }

  throw new Error(`Failed to get request, Shopify returned ${result.status} ${result.statusText}`);
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
